import React, { useState } from 'react';
import LegalLayout from '../components/LegalLayout';

const TermsOfUse = () => {
  // Date courante pour les besoins de démonstration
  const today = new Date();
  
  // Versions historiques de la politique avec leur contenu spécifique
  const policyVersions = [
    { 
      date: today, 
      notes: "Version actuelle - Politique d'utilisation en vigueur",
      content: {
        introduction: "Bienvenue sur Clicdroits.info. En utilisant notre site et nos services, vous acceptez les conditions d'utilisation décrites ci-dessous. Nous vous invitons à les lire attentivement afin de comprendre la nature de nos services et leurs limites.",
        sections: [
          {
            title: "Politique d'utilisation",
            content: "Cette politique d'utilisation établit les règles et conditions d'utilisation de nos services."
          }
        ]
      }
    }
  ];

  const [selectedDate, setSelectedDate] = useState(today);

  // Trouver la version de politique correspondant à la date sélectionnée
  const selectedVersion = policyVersions.find(version => 
    version.date.getTime() === selectedDate.getTime()
  ) || policyVersions[policyVersions.length - 1];

  // Obtenir la date formatée
  const formattedDate = selectedDate.toLocaleDateString('fr-FR', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  // Fonction pour vérifier si une date est aujourd'hui
  const isToday = (date) => {
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  return (
    <LegalLayout 
      title="Politique d'Utilisation"
      lastUpdated={formattedDate}
    >
      <div className="space-y-8">
        {/* Sélecteur de version */}
        <section className="mb-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-medium text-gray-800 mb-2">Historique des modifications</h3>
            <div className="flex flex-wrap gap-2">
              {policyVersions.map((version, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedDate(version.date)}
                  className={`px-3 py-1 rounded-md text-sm ${
                    selectedDate.getTime() === version.date.getTime()
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  {isToday(version.date) 
                    ? "Aujourd'hui" 
                    : version.date.toLocaleDateString('fr-FR', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })
                  }
                </button>
              ))}
            </div>
            <p className="mt-2 text-sm text-gray-600">
              {selectedVersion.notes}
            </p>
          </div>
        </section>

        {/* Si c'est une ancienne version, afficher un avertissement */}
        {!isToday(selectedDate) && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-amber-700">
              <strong>Note:</strong> Vous consultez une version historique de notre politique d'utilisation. 
              <button 
                onClick={() => setSelectedDate(today)} 
                className="ml-2 text-blue-600 hover:text-blue-800 underline"
              >
                Voir la version actuelle
              </button>
            </p>
          </div>
        )}

        {/* Contenu spécifique à la version sélectionnée */}
        <section>
          <p className="text-base sm:text-lg text-gray-600 mb-4">
            {selectedVersion.content.introduction}
          </p>
        </section>

        {/* Sections de la version sélectionnée */}
        {selectedVersion.content.sections.map((section, index) => (
          <section key={index}>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              {section.title}
            </h2>
            <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
              <p className="text-gray-600">{section.content}</p>
            </div>
          </section>
        ))}

        {/* Afficher le contenu complet seulement pour la version actuelle */}
        {isToday(selectedDate) && (
          <>
            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Nature de nos services
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="text-gray-600 mb-3">
                  <strong className="text-gray-800">Clicdroits.info n'est pas un cabinet d'avocats et ne fournit pas de conseils juridiques.</strong> Notre plateforme 
                  offre des outils d'information et d'orientation dans le domaine du droit de la famille au Québec.
                </p>
                <p className="text-gray-600 mb-3">
                  Notre approche est différente des sites juridiques traditionnels :
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                  <li>
                    Plutôt que de vous laisser chercher parmi des catégories juridiques complexes, notre tableau de bord et 
                    notre outil ClicoSearch vous aident à trouver rapidement l'information pertinente pour votre situation.
                  </li>
                  <li>
                    Nous vulgarisons l'information juridique pour la rendre accessible, sans pour autant la dénaturer.
                  </li>
                  <li>
                    Nous proposons des estimations, des calendriers et des listes de vérification pour vous aider à mieux comprendre 
                    votre situation et les démarches possibles.
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Limites de nos services
              </h2>
              <div className="bg-yellow-50 p-6 rounded-xl border border-yellow-200">
                <p className="text-gray-700 mb-3">
                  En utilisant nos services, vous reconnaissez et acceptez les limites suivantes :
                </p>
                <ul className="list-disc pl-6 space-y-3 text-gray-700">
                  <li>
                    <strong>Pas de conseil juridique personnalisé :</strong> Nos informations sont générales et ne 
                    constituent pas des conseils juridiques adaptés à votre situation spécifique.
                  </li>
                  <li>
                    <strong>Pas de relation avocat-client :</strong> L'utilisation de nos services ne crée pas une 
                    relation avocat-client entre vous et Clicdroits.info ou ses représentants.
                  </li>
                  <li>
                    <strong>Pas de pièce justificative :</strong> Les informations, estimations et documents fournis 
                    sur notre site <strong>ne doivent pas être utilisés comme pièces justificatives devant un tribunal</strong>.
                  </li>
                  <li>
                    <strong>Estimations approximatives :</strong> Toutes les estimations (montants de pension alimentaire, 
                    coûts de procédure, délais, etc.) sont fournies à titre indicatif seulement et peuvent différer 
                    significativement des décisions réelles des tribunaux.
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Transparence de nos algorithmes
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="text-gray-600 mb-3">
                  Notre barre de recherche ClicoSearch est actuellement en développement continu. Nous nous engageons à une transparence totale concernant son fonctionnement :
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                  <li>
                    Les détails techniques de notre algorithme seront publiés sur un site dédié, accessible à tous.
                  </li>
                  <li>
                    Bien que la documentation puisse être complexe pour les non-professionnels, notre objectif est que tout expert juridique ou technique 
                    puisse examiner si l'algorithme prend en compte des facteurs appropriés et pertinents.
                  </li>
                  <li>
                    Nous reconnaissons que ce service est en développement et qu'il n'est pas parfait. Nous travaillons continuellement à l'améliorer 
                    pour qu'il se rapproche autant que possible d'une solution optimale.
                  </li>
                  <li>
                    Vos retours nous sont précieux pour améliorer la pertinence et la précision de notre système.
                  </li>
                </ul>
                <p className="text-gray-600 mt-3 italic">
                  Notre engagement envers la transparence vise à établir la confiance dans notre système et à garantir 
                  que nos utilisateurs comprennent comment les informations leur sont présentées.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Consultation d'un avocat
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="text-gray-600 mb-3">
                  Nous vous encourageons fortement à consulter un avocat pour :
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                  <li>
                    Recevoir des conseils juridiques personnalisés adaptés à votre situation spécifique.
                  </li>
                  <li>
                    Évaluer vos chances réelles de succès dans une procédure judiciaire.
                  </li>
                  <li>
                    Définir une stratégie juridique adaptée à vos objectifs et circonstances.
                  </li>
                  <li>
                    Vous représenter devant les tribunaux ou dans le cadre d'une négociation.
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Données personnelles et confidentialité
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="text-gray-600">
                  Les informations que vous saisissez sur notre site sont traitées conformément à notre{' '}
                  <a href="/privacy-policy" className="text-primary hover:text-primary/80 underline">
                    Politique de confidentialité
                  </a>. Nous vous recommandons d'en prendre connaissance.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Contact
              </h2>
              <div className="bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                <p className="text-gray-600">
                  Si vous avez des questions concernant ces conditions d'utilisation, veuillez nous contacter à{' '}
                  <a 
                    href="mailto:raouf@clicdroits.info" 
                    className="text-primary hover:text-primary/80 underline"
                  >
                    raouf@clicdroits.info
                  </a>
                </p>
              </div>
            </section>
          </>
        )}
      </div>
    </LegalLayout>
  );
};

export default TermsOfUse;
